/**
 * @xiangqi/protocol — the single source of truth for everything that crosses
 * the wire between apps/server and clients: REST bodies, WebSocket frames,
 * error codes and shared constants. Server validates with the zod schemas;
 * clients import the same types.
 */
import { z } from 'zod';
export const PROTOCOL_VERSION = 1;
/** Server pings idle sockets at this interval; two misses = dead. */
export const HEARTBEAT_MS = 15_000;
/** How long a disconnected player may resume before forfeiting (Phase 4). */
export const RESUME_WINDOW_MS = 90_000;
/** WS auth tickets are one-time and short-lived. */
export const TICKET_TTL_S = 30;
/** MVP default clock: 10 minutes + 5 seconds increment. */
export const DEFAULT_CLOCK = { initial_ms: 600_000, increment_ms: 5_000 };
// ---------------------------------------------------------------- primitives
export const rulesetSchema = z.enum([
    'standard',
    'blind',
    'blind_si_chua',
    'blind_tuong_vuong',
    'blind_royal', // legacy: cả Sĩ Chúa lẫn Tượng Vương (không tạo mới từ UI)
]);
/** Nhãn hiển thị + alias sản phẩm cho từng mode. */
export const MODE_INFO = {
    standard: { label: 'Cờ Tướng', alias: 'xiangqi_standard' },
    blind: { label: 'Cờ Úp', alias: 'hidden_xiangqi' },
    blind_si_chua: { label: 'Cờ Úp Sĩ Chúa', alias: 'hidden_xiangqi_si_chua' },
    blind_tuong_vuong: { label: 'Cờ Úp Tượng Vương', alias: 'hidden_xiangqi_tuong_vuong' },
    blind_royal: { label: 'Sĩ Chúa & Tượng Vương (cũ)', alias: 'hidden_xiangqi_royal_legacy' },
};
// ---------------------------------------------------------------- handicap
export const handicapTypeSchema = z.enum(['none', 'piece', 'moves', 'asym_royal']);
export const handicapPieceSchema = z.enum([
    'left_chariot',
    'right_chariot',
    'left_horse',
    'right_horse',
    'left_cannon',
    'right_cannon',
]);
export const timeHandicapSchema = z.object({
    giver_ms: z.number().int().min(30_000).max(7200_000),
    receiver_ms: z.number().int().min(30_000).max(7200_000),
});
export const handicapConfigSchema = z.object({
    type: handicapTypeSchema.default('none'),
    /** Bên chấp: 'red' | 'black' (mặc định 'red'). */
    giver_side: z.enum(['red', 'black']).default('red'),
    /** Danh sách quân chấp khi type === 'piece'. */
    pieces: z.array(handicapPieceSchema).default([]),
    /** Số nước chấp khi type === 'moves' hoặc 'asym_royal' (2, 2.5, 6, 7). */
    moves_count: z.union([z.literal(2), z.literal(2.5), z.literal(6), z.literal(7)]).default(2),
    /** Ván hiện tại đi bao nhiêu nước chấp (dành cho 2.5: 2 hoặc 3). */
    current_moves: z.union([z.literal(2), z.literal(3), z.literal(6), z.literal(7)]).optional(),
    /** Cấu hình chấp giờ: thời gian ban đầu khác nhau giữa bên chấp và bên nhận. */
    time: timeHandicapSchema.optional(),
});
export function toEngineHandicap(ruleset, handicap) {
    if (!handicap || handicap.type === 'none')
        return undefined;
    const giver = handicap.giver_side ?? 'red';
    const recipient = giver === 'red' ? 'black' : 'red';
    if (handicap.type === 'piece') {
        if (ruleset !== 'standard')
            return undefined;
        const removed = [];
        for (const p of handicap.pieces ?? []) {
            if (giver === 'red') {
                if (p === 'left_chariot')
                    removed.push(0);
                else if (p === 'left_horse')
                    removed.push(1);
                else if (p === 'left_cannon')
                    removed.push(19);
                else if (p === 'right_cannon')
                    removed.push(25);
                else if (p === 'right_horse')
                    removed.push(7);
                else if (p === 'right_chariot')
                    removed.push(8);
            }
            else {
                if (p === 'left_chariot')
                    removed.push(89);
                else if (p === 'left_horse')
                    removed.push(88);
                else if (p === 'left_cannon')
                    removed.push(70);
                else if (p === 'right_cannon')
                    removed.push(64);
                else if (p === 'right_horse')
                    removed.push(82);
                else if (p === 'right_chariot')
                    removed.push(81);
            }
        }
        return {
            handicap_side: recipient,
            removed_squares: removed,
            opening_moves: 0,
        };
    }
    if (handicap.type === 'moves') {
        if (ruleset !== 'standard')
            return undefined;
        const moves = handicap.current_moves ?? (handicap.moves_count === 2.5 ? 2 : handicap.moves_count);
        return {
            handicap_side: recipient,
            opening_moves: moves,
        };
    }
    if (handicap.type === 'asym_royal') {
        if (ruleset !== 'blind_tuong_vuong' && ruleset !== 'blind_si_chua') {
            return undefined;
        }
        const moves = ruleset === 'blind_tuong_vuong' ? 6 : 7;
        return {
            royal_side: giver,
            handicap_side: recipient,
            opening_moves: moves,
        };
    }
    return undefined;
}
// ---------------------------------------------------------------- bot
export const botDifficultySchema = z.enum(['easy', 'medium', 'hard', 'expert', 'master']);
/** Redis queue the server pushes bot work into; BotMaster BRPOPs it. */
export const BOT_INVITES_KEY = 'bot:invites';
export const botPairKey = (pairId) => `bot:pair:${pairId}`;
export const sideSchema = z.enum(['red', 'black']);
export const squareSchema = z.number().int().min(0).max(89);
export const moveSchema = z.object({
    from: squareSchema,
    to: squareSchema,
});
export const clockConfigSchema = z.object({
    initial_ms: z
        .number()
        .int()
        .min(60_000)
        .max(2 * 60 * 60_000),
    increment_ms: z.number().int().min(0).max(60_000),
});
// ---------------------------------------------------------------- REST
export const guestBodySchema = z.object({
    display_name: z.string().trim().min(1).max(30).optional(),
});
export const registerBodySchema = z.object({
    username: z
        .string()
        .regex(/^[a-zA-Z0-9_]{3,20}$/, 'username: 3-20 ký tự chữ, số, gạch dưới'),
    password: z.string().min(8).max(72),
    display_name: z.string().trim().min(1).max(30).optional(),
});
export const loginBodySchema = z.object({
    username: z.string().min(1).max(20),
    password: z.string().min(1).max(72),
});
export const refreshBodySchema = z.object({
    refresh_token: z.string().min(16).max(128),
});
export const createRoomBodySchema = z.object({
    ruleset: rulesetSchema.default('standard'),
    clock: clockConfigSchema.default(DEFAULT_CLOCK),
    handicap: handicapConfigSchema.optional(),
    /** Đặt để chơi với bot: server enqueue lời mời cho BotMaster. */
    vs_bot: z
        .object({
        profile: botDifficultySchema,
        /** Side người muốn cầm; null = ngẫu nhiên. */
        human_side: sideSchema.nullable().default(null),
    })
        .optional(),
    /** Cho phép người ngoài xem ván (dev/bot-vs-bot). */
    spectatable: z.boolean().default(false),
    /** Ghim màu cho CHỦ PHÒNG (bot-pair dùng; null = ngẫu nhiên). */
    host_side: sideSchema.nullable().default(null),
});
export const matchmakingFindSchema = z.object({
    ruleset: rulesetSchema.default('standard'),
    clock: clockConfigSchema.default(DEFAULT_CLOCK),
    handicap: handicapConfigSchema.optional(),
});
export const clientFrameSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('auth'), ticket: z.string().min(8).max(128) }),
    z.object({
        type: z.literal('subscribe'),
        match_id: z.uuid(),
        /** Last stream_seq the client has; server replays newer frames. */
        cursor: z.number().int().min(0).optional(),
    }),
    z.object({
        type: z.literal('game.move'),
        command_id: z.uuid(),
        /** Ply the client believes it is making (optimistic concurrency). */
        ply: z.number().int().min(0),
        move: moveSchema,
    }),
    z.object({ type: z.literal('game.resign') }),
    z.object({ type: z.literal('game.draw_offer') }),
    z.object({ type: z.literal('game.draw_response'), accept: z.boolean() }),
    z.object({ type: z.literal('game.rematch_offer') }),
    z.object({ type: z.literal('game.rematch_response'), accept: z.boolean() }),
    z.object({ type: z.literal('game.chat'), text: z.string().trim().min(1).max(200) }),
    z.object({ type: z.literal('ping'), nonce: z.string().max(64).optional() }),
]);
//# sourceMappingURL=index.js.map